import { AnimatePresence } from "framer-motion";
import {
  startTransition,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ErrorBoundary } from "react-error-boundary";

import {
  JOURNEY_MAP_VIEW_ERRORED,
  STEP_TO_MAP,
} from "@context/journey.context";
import { useDeleteCareerMatches } from "@features/career-matches/api/use-delete-career-matches";
import { Loader } from "@features/ui";
import { reportReactErrorBoundary } from "@src/observability/reporting";

import { useCreateUserJourney } from "./api/use-create-user-journey";
import { useJourneySteps } from "./api/use-journey-steps";
import { usePrefetchPathway } from "./api/use-prefetch-pathway";
import { useUserJourney } from "./api/use-user-journey";
import { FeedbackButton } from "./components";
import { JourneyMapError } from "./components/journey-error";
import { JourneyViewToggle } from "./components/journey-view.toggle";
import { useJourney } from "./hooks/use-journey";
import {
  CareerCompareSheet,
  CareerMatchesSheet,
  JourneyMapShortcutsView,
} from "./views";
import { BudgetingGameSheet } from "./views/budgeting-game.sheet";
import { GoalsSheet } from "./views/goals.sheet";
import { HighSchoolSheet } from "./views/high-school.sheet";
import { InterestInventorySheet } from "./views/interest-inventory.sheet";
import { JourneyMap } from "./views/journey-map";
import { JourneyMapLoading } from "./views/journey-map.loading";
import { PostHighSchoolSheet } from "./views/post-high-school.sheet";
import { ShareSheet } from "./views/share.sheet";
import { SkillsSheet } from "./views/skills.sheet";

import "./index.scss";

export const JourneyMapPage = () => {
  const pageRef = useRef();

  const [triggerNextMap, setTriggerNextMap] = useState();
  const [triggerMapAnimation, setTriggerMapAnimation] = useState();
  const [showSheet, setShowSheet] = useState();
  const [pageHeight, setPageHeight] = useState(0);
  const [isPlanShared, setIsPlanShared] = useState(false);
  const [pendingStep, setPendingStep] = useState();

  useLayoutEffect(() => {
    if (pageRef.current) {
      setPageHeight(pageRef.current.offsetHeight);
    }
  }, [pageRef.current]);

  const {
    currentMap,
    isJourneyLoading,
    journeyMapErrored,
    journeyMapView,
    nextMapUnlocked,
    requestMapChange,
    requestStepChange,
    setJourneyMapView,
    unlockedSteps,
  } = useJourney();
  const { data: journeySteps } = useJourneySteps();
  const { data: userJourney, isPending, isError } = useUserJourney();
  const {
    isPending: createUserJourneyPending,
    isError: createUserJourneyError,
    mutateAsync: createUserJourney,
  } = useCreateUserJourney();
  const { mutateAsync: deleteCareerMatches, isPending: deletingCareerMatches } =
    useDeleteCareerMatches();

  usePrefetchPathway(userJourney.currentJourneyCareer?.id);

  useEffect(() => {
    if (!isPending && userJourney.status === 404) {
      createUserJourney();
    }
  }, [isPending, userJourney.status]);

  useEffect(() => {
    if (nextMapUnlocked && triggerNextMap && !triggerMapAnimation) {
      requestMapChange(currentMap + 1);
      setTriggerNextMap(() => false);
    }
  }, [nextMapUnlocked, triggerNextMap, triggerMapAnimation]);

  useEffect(() => {
    if (
      userJourney.surveyCompletedAt &&
      !showSheet &&
      journeyMapView &&
      currentMap === 1
    ) {
      setTriggerMapAnimation(STEP_TO_MAP.INT_INV);
    }
  }, [userJourney, showSheet, currentMap]);

  useEffect(() => {
    if (isPlanShared) {
      const timeoutId = setTimeout(() => {
        setIsPlanShared(false);
      }, 5000);

      return () => clearTimeout(timeoutId);
    }
  }, [isPlanShared]);

  useEffect(() => {
    if (pendingStep) {
      moveToStep(pendingStep).then((success) => {
        if (success) {
          setPendingStep();
        }
      });
    }
  }, [unlockedSteps]);

  useEffect(() => {
    const eventName = showSheet ? "sheetopened" : "sheetclosed";
    dispatchEvent(new CustomEvent(eventName));
  }, [showSheet]);

  const closeSheet = useCallback(() => setShowSheet(), [setShowSheet]);

  const onMapAnimationComplete = () => setTriggerMapAnimation();

  const moveToStep = async (journeyStep) => {
    if (await requestStepChange(journeyStep)) {
      startTransition(() => {
        setShowSheet(journeyStep.code);
      });
      return true;
    }

    return false;
  };

  const moveToMap = useCallback(
    async (destinationMap) => {
      startTransition(() => {
        if (destinationMap) {
          requestMapChange(destinationMap);
        } else {
          setTriggerNextMap(true);
        }
      });
    },
    [requestMapChange, setTriggerNextMap],
  );

  const closeAndMoveToMap = useCallback(
    (destinationMap) => {
      closeSheet();
      moveToMap(destinationMap);
    },
    [closeSheet, moveToMap],
  );

  const onStartOverFromCareerMatches = async () => {
    closeSheet();
    await moveToMap(STEP_TO_MAP.INT_INV);
    await moveToStep(journeySteps.stepsByCode.INT_INV);
    // Note: this is done here so that the career matches sheet will already be
    // closed and the career matches won't be immediately re-loaded.
    await deleteCareerMatches();
  };

  const onExploreMatches = async () => {
    if (journeyMapView) {
      closeAndMoveToMap();
    } else {
      closeSheet();
      await moveToMap(STEP_TO_MAP.MATCHES);
      await moveToStep(journeySteps.stepsByCode.MATCHES);
    }
  };

  const onGoalsSubmitted = async () => {
    closeSheet();
    if (!journeyMapView) {
      await moveToMap(STEP_TO_MAP.SHARE);
    }

    // Let the map know it should move Dash to the Share step, which will then
    // automatically open the Share dialog.
    setTriggerMapAnimation(journeySteps.stepsByCode.SHARE.stepNum);
  };

  const onCreateBudget = async () => {
    closeSheet();
    if (!journeyMapView) {
      await moveToStep(journeySteps.stepsByCode.BUDGET);
    }

    setTriggerMapAnimation(journeySteps.stepsByCode.COMPARE.stepNum);
  };

  const onShare = () => {
    setIsPlanShared(true);
  };

  // TODO: Set the error and loading screen
  if ((isError && userJourney.status !== 404) || createUserJourneyError) {
    return <div>Error</div>;
  } else if (isJourneyLoading || isPending || createUserJourneyPending) {
    return <JourneyMapLoading />;
  }

  const onToggleMapView = () => {
    setJourneyMapView(!journeyMapView);
  };

  const sheets = {
    [journeySteps.stepsByCode.INT_INV.code]: {
      sheet: InterestInventorySheet,
      props: {
        onExploreMatches,
      },
    },
    [journeySteps.stepsByCode.MATCHES.code]: {
      sheet: CareerMatchesSheet,
      props: {
        onCareerSelected: () =>
          // Move to the next map once the user journey is updated
          closeAndMoveToMap(),
        onStartOver: onStartOverFromCareerMatches,
      },
    },
    [journeySteps.stepsByCode.SKILLS.code]: { sheet: SkillsSheet },
    [journeySteps.stepsByCode.HIGH_SCH.code]: { sheet: HighSchoolSheet },
    [journeySteps.stepsByCode.POST_HIGH_SCH.code]: {
      sheet: PostHighSchoolSheet,
    },
    [journeySteps.stepsByCode.GOALS.code]: {
      sheet: GoalsSheet,
      props: {
        onRemovePlan: () => closeAndMoveToMap(STEP_TO_MAP.MATCHES),
        onSubmit: onGoalsSubmitted,
      },
    },
    [journeySteps.stepsByCode.SHARE.code]: {
      sheet: ShareSheet,
      props: {
        onShare,
      },
    },
    [journeySteps.stepsByCode.BUDGET.code]: {
      sheet: BudgetingGameSheet,
      props: {
        onBudgetingGameComplete: closeAndMoveToMap,
      },
    },
    [journeySteps.stepsByCode.COMPARE.code]: {
      sheet: CareerCompareSheet,
      props: {
        onCreateBudget,
        onViewPathway: () => closeAndMoveToMap(),
      },
    },
  };

  return (
    <div className="journey-map-page" ref={pageRef}>
      <Loader show={deletingCareerMatches} text={"removing matches..."} />
      <Loader show={pendingStep} text={"please wait..."} />
      <FeedbackButton />
      {!journeyMapErrored && (
        <JourneyViewToggle onToggle={onToggleMapView} value={journeyMapView} />
      )}
      <AnimatePresence mode="popLayout">
        {journeyMapView ? (
          // Currently need Suspense here because the useDetectGpu hook
          // suspends.
          <ErrorBoundary
            FallbackComponent={JourneyMapError}
            onReset={() => setJourneyMapView(JOURNEY_MAP_VIEW_ERRORED)}
            onError={(error, info) => reportReactErrorBoundary({ error, info })}
          >
            <Suspense>
              <JourneyMap
                pageHeight={pageHeight}
                currentMap={currentMap}
                triggerMapAnimation={triggerMapAnimation}
                moveToMap={moveToMap}
                moveToStep={moveToStep}
                onMapAnimationComplete={onMapAnimationComplete}
                unlockedSteps={unlockedSteps}
                isPlanShared={isPlanShared}
              />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <JourneyMapShortcutsView
            journeySteps={journeySteps}
            moveToStep={moveToStep}
            unlockedSteps={unlockedSteps}
            moveToMap={moveToMap}
          />
        )}
      </AnimatePresence>
      {Object.entries(sheets).map(
        ([code, { sheet: SheetComponent, props = {} }]) => (
          <SheetComponent
            key={code}
            open={showSheet === code}
            onClose={closeSheet}
            {...props}
          />
        ),
      )}
    </div>
  );
};
